import { Antd6Provider } from '@components/shared/Antd6Provider';
import { Modal } from 'antd-v6';
import { AlertTriangle, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface ConfirmModalProps {
  readonly open: boolean;
  readonly title: ReactNode;
  readonly message: ReactNode;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  /** Style the confirm button as destructive (red). Use for delete/discard. */
  readonly danger?: boolean;
  /** Show a spinner on the confirm button while an async action resolves. */
  readonly loading?: boolean;
  readonly confirmText?: ReactNode;
  readonly cancelText?: ReactNode;
  readonly zIndex?: number;
}

/** Declarative confirmation dialog (antd-v6). For imperative `await confirm()`, see `useConfirm`. */
export function ConfirmModal({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  danger = false,
  loading = false,
  confirmText,
  cancelText,
  zIndex = 1031,
}: ConfirmModalProps) {
  const { t } = useTranslation();

  return (
    <Antd6Provider mode="light">
      <Modal
        open={open}
        onCancel={onCancel}
        footer={null}
        width={440}
        centered
        zIndex={zIndex}
        destroyOnHidden
        closable={!loading}
        keyboard={!loading}
        maskClosable={!loading}
      >
        <div className="flex gap-3 items-start">
          <AlertTriangle className="text-sy-yellow-40 flex-shrink-0 mt-0.5" size={22} />
          <div>
            <h2 className="text-base font-semibold mb-2 text-sy-neutral-100">{title}</h2>
            <div className="text-sm text-sy-neutral-100 mb-0">{message}</div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            data-testid="confirm-modal-cancel"
            className="rounded px-4 py-2 text-sm border border-sy-neutral-20 hover:bg-sy-neutral-10 transition-colors disabled:opacity-60"
          >
            {cancelText ?? t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            data-testid="confirm-modal-confirm"
            className={`inline-flex items-center justify-center gap-2 rounded px-4 py-2 text-sm text-white transition-colors disabled:opacity-60 ${
              danger ? 'bg-sy-red-60 hover:bg-sy-red-70' : 'bg-sy-blue-60 hover:bg-sy-blue-70'
            }`}
          >
            {loading && <Loader2 className="animate-spin" size={16} aria-hidden />}
            {confirmText ?? t('common.confirm')}
          </button>
        </div>
      </Modal>
    </Antd6Provider>
  );
}
